import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { AuthContext, UnreadMessagesContext } from '../App';
import NotificationCenter from './NotificationCenter';
import { 
  User, 
  LogOut, 
  Settings, 
  Briefcase, 
  DollarSign,
  MessageSquare,
  LayoutDashboard,
  ChevronDown,
  Calendar,
  Clock,
  Gift,
  Check,
  Award,
  Star,
  Power,
  FileText,
  Wallet,
  Send,
  ArrowDownToLine,
  AlertTriangle
} from 'lucide-react';
import OnlineStatus from './OnlineStatus';
import { BACKEND_URL } from '../config/api';
import './TopNav.css';

const API = `${BACKEND_URL}/api`;

const TopNav = () => {
  const { user, setUser, logout } = useContext(AuthContext);
  const { unreadCount } = useContext(UnreadMessagesContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [heroProfile, setHeroProfile] = useState(null);
  const [expandedGroup, setExpandedGroup] = useState(null);

  const handleLogout = async () => {
    await logout();
  };

  // Fetch hero profile
  useEffect(() => {
    const fetchHeroProfile = async () => {
      try {
        const response = await axios.get(`${API}/task-hero/profile`, { withCredentials: true });
        setHeroProfile(response.data);
      } catch (error) {
        console.error('Failed to fetch hero profile:', error);
      }
    };

    if (user) {
      fetchHeroProfile();
    }
  }, [user]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isUserMenuOpen) {
        const menuContainer = document.querySelector('.user-menu-container');
        if (menuContainer && !menuContainer.contains(event.target)) {
          setIsUserMenuOpen(false);
        }
      }
    };

    if (isUserMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isUserMenuOpen]);

  const toggleAvailability = async () => {
    try {
      await axios.patch(`${API}/task-hero/availability`, {}, { withCredentials: true });
      setHeroProfile({ ...heroProfile, is_available: !heroProfile.is_available });
    } catch (error) {
      console.error('Failed to toggle availability:', error);
    }
  };

  if (!user) {
    return (
      <>
        <div style={{
          position: 'fixed', top: '-1px', left: 0, right: 0, height: '64px',
          background: 'linear-gradient(135deg, rgba(240, 243, 255, 0.5) 0%, rgba(248, 245, 255, 0.45) 50%, rgba(245, 250, 255, 0.5) 100%)',
          backdropFilter: 'blur(40px) saturate(200%) brightness(1.02)',
          WebkitBackdropFilter: 'blur(40px) saturate(200%) brightness(1.02)',
          borderBottom: '1px solid rgba(200, 210, 230, 0.35)',
          boxShadow: '0 1px 0 rgba(255,255,255,0.5), 0 4px 20px rgba(0,0,0,0.04), inset 0 1px 0 rgba(255,255,255,0.85)',
          zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '0 24px', boxSizing: 'border-box',
        }}>
          <div className="nav-logo" onClick={() => navigate('/')} style={{ cursor: 'pointer', display: 'flex', alignItems: 'flex-start', gap: '4px' }}>
            <picture>
              <source srcSet="/logo-dark.webp" type="image/webp" />
              <img src="/logo-dark.png" alt="taskeri®" className="logo-image" width="107" height="32" style={{ height: '32px', width: 'auto' }} />
            </picture>
            <picture>
              <source srcSet="/flag-ca.webp" type="image/webp" />
              <img 
                src="/flag-ca.png" 
                alt="Canada" 
                className="canada-flag"
                width="20" height="11"
                style={{ height: '11px', width: 'auto', borderRadius: '1px', marginTop: '2px' }}
              />
            </picture>
          </div>
          <button
            data-testid="topnav-signin-btn"
            onClick={() => { window.location.href = `${window.location.origin}/api/auth/google/login`; }}
            style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              padding: '8px 20px', borderRadius: '10px',
              border: '0px solid #e2e8f0', background: '#0f172a',
              color: '#fff', fontSize: '14px', fontWeight: 600, cursor: 'pointer',
            }}
          >
            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" width="16" height="16" style={{ flexShrink: 0 }} />
            Sign In
          </button>
        </div>

      </>
    );
  }

  // Detect if we're in admin panel
  const isAdminPanel = location.pathname.startsWith('/admin');
  
  // Get current active tab from URL, with fallback to user's primary role
  const searchParams = new URLSearchParams(location.search);
  const tabFromUrl = searchParams.get('tab');
  // Use URL tab if present, otherwise fall back to user's primary_role, default to 'client'
  const currentTab = tabFromUrl || user.primary_role || 'client';
  
  // Get role preference (client, hero, or both)
  const rolePreference = user.role_preference || 'both';
  const showClientFeatures = rolePreference === 'client' || rolePreference === 'both';
  const showHeroFeatures = rolePreference === 'hero' || rolePreference === 'both';
  
  // Build mode switches based on role preference
  const modeOptions = [];
  if (rolePreference === 'both') {
    modeOptions.push(
      { icon: Briefcase, label: 'Client Mode', path: '/dashboard?tab=client', isRoleSwitch: true, roleType: 'client' },
      { icon: Award, label: 'Hero Mode', path: '/dashboard?tab=hero', isRoleSwitch: true, roleType: 'hero' }
    );
  }
  
  // Build money menu based on role
  const moneyOptions = [
    { icon: Wallet, label: 'Wallet', path: '/wallet' },
  ];
  if (showHeroFeatures) {
    moneyOptions.push({ icon: DollarSign, label: 'Earnings', path: '/my-earnings' });
  }
  moneyOptions.push(
    { icon: Send, label: 'Transfers', path: '/money-transfers' },
    { icon: ArrowDownToLine, label: 'Transactions', path: '/transactions' },
    { icon: FileText, label: 'Invoices', path: '/invoices' }
  );
  
  // Grouped menu structure - keeps the menu shorter
  const menuStructure = {
    main: [
      { icon: User, label: 'Profile', path: '/profile' },
      ...(showHeroFeatures ? [{ icon: Clock, label: 'Availability', path: '/calendar?tab=availability' }] : []),
    ],
    modes: modeOptions,
    activity: [
      ...(showHeroFeatures ? [{ icon: Briefcase, label: 'Jobs Applied', path: '/jobs-applied' }] : []),
      { icon: Star, label: 'Reviews', path: '/reviews' },
    ],
    money: moneyOptions,
    account: [
      { icon: Gift, label: 'Refer & Earn', path: '/referrals' },
      { icon: AlertTriangle, label: 'Disputes', path: '/my-disputes' },
    ],
  };

  const handleLogoClick = () => {
    if (isAdminPanel) {
      navigate('/ti-admin/dashboard');
    } else {
      navigate('/dashboard');
    }
  };

  const toggleGroup = (group) => {
    setExpandedGroup(expandedGroup === group ? null : group);
  };

  const renderMenuItem = (item) => {
    const Icon = item.icon;
    const isActive = location.pathname === item.path || 
      (item.path.includes('?') && location.pathname + location.search === item.path);
    const isActiveRole = item.isRoleSwitch && item.roleType === currentTab && location.pathname === '/dashboard';
    
    return (
      <button
        key={item.path}
        className={`dropdown-item ${isActive || isActiveRole ? 'active' : ''}`}
        onClick={() => {
          navigate(item.path);
          setIsUserMenuOpen(false);
          setExpandedGroup(null);
        }}
      >
        <Icon size={18} />
        <span>{item.label}</span>
        {isActiveRole && <Check size={16} style={{ marginLeft: 'auto', color: '#8448fc' }} />}
      </button>
    );
  };

  return (
    <>
      {/* Spacer for fixed nav */}
      {/* <div className="top-nav-spacer" /> */}
      
      <div className="top-nav">
        <div className="top-nav-content">
          {/* Logo */}
          <div className="nav-logo" onClick={handleLogoClick}>
            <picture>
              <source srcSet="/logo-dark.webp" type="image/webp" />
              <img src="/logo-dark.png" alt="taskeri®" className="logo-image" />
            </picture>
          </div>

          {/* Right Side Menu */}
          <div className="nav-right">
            {/* Messages - Top Level Navigation */}
            {!isAdminPanel && (
              <button 
                className={`nav-messages-btn ${location.pathname === '/messages' ? 'active' : ''}`}
                onClick={() => navigate('/messages')}
                title="Messages"
              >
                <div className="messages-icon-wrapper">
                  <MessageSquare size={20} />
                  {unreadCount > 0 && (
                    <span className="unread-badge">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </div>
                <span className="nav-messages-label">Messages</span>
              </button>
            )}

            {/* Mode Switcher - Only show when role preference is 'both' */}
            {!isAdminPanel && rolePreference === 'both' && (
              <div className="mode-switcher">
                <button 
                  className={`mode-toggle ${currentTab === 'client' ? 'mode-client' : 'mode-hero'}`}
                  onClick={() => {
                    const newTab = currentTab === 'client' ? 'hero' : 'client';
                    // Save mode to backend for cross-device persistence
                    axios.post(`${API}/user/selected-mode`, { selected_mode: newTab }, { withCredentials: true })
                      .catch(err => console.error("Failed to save mode:", err));
                    navigate(`/dashboard?tab=${newTab}`);
                  }}
                  title={`Switch to ${currentTab === 'client' ? 'Hero' : 'Client'} Mode`}
                >
                  <span className="mode-status-dot"></span>
                  {currentTab === 'client' ? (
                    <Briefcase size={14} />
                  ) : (
                    <Award size={14} />
                  )}
                  <span className="mode-label">{currentTab === 'client' ? 'Client' : 'Hero'}</span>
                  <Power size={10} className="switch-icon" />
                </button>
              </div>
            )}

            {/* Notification Bell */}
            <NotificationCenter />

            {/* User Menu */}
            <div className="user-menu-container">
              <button 
                className="user-menu-trigger"
                onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
              >
                {user.picture ? (
                  <img src={user.picture} alt={user.name} className="user-avatar" />
                ) : (
                  <div className="user-avatar-placeholder">
                    {(user.name || 'U')[0].toUpperCase()}
                  </div>
                )}
                <span className="user-name">{user.name}</span>
                <ChevronDown size={16} className={`chevron ${isUserMenuOpen ? 'open' : ''}`} />
              </button>

              {/* Dropdown Menu */}
              {isUserMenuOpen && (
                <>
                  <div className="menu-overlay" onClick={() => setIsUserMenuOpen(false)} />
                  <div className="user-dropdown">
                    <div className="dropdown-header-v2">
                      <div className="header-avatar">
                        {user.picture ? (
                          <img src={user.picture} alt={user.name} />
                        ) : (
                          <div className="avatar-placeholder">
                            {(user.name || 'U')[0].toUpperCase()}
                          </div>
                        )}
                        <div className="online-indicator"></div>
                      </div>
                      <div className="header-info">
                        <h4 className="header-name">{user.name}</h4>
                        <div className="header-status">
                          <OnlineStatus userId={user.id} showLabel={true} size="sm" />
                        </div>
                        <p className="header-email">{user.email}</p>
                      </div>
                    </div>

                    <div className="dropdown-divider" />
                    
                    <div className="dropdown-items">
                      {/* Dashboard */}
                      {menuStructure.main.map(renderMenuItem)}
                      
                      {/* Mode Switcher - Only show when role is 'both' */}
                      {menuStructure.modes.length > 0 && (
                        <>
                          <div className="dropdown-divider" />
                          <div className="menu-group-label">Switch Mode</div>
                          {menuStructure.modes.map(renderMenuItem)}
                        </>
                      )}
                      
                      {/* Activity Group */}
                      <div className="dropdown-divider" />
                      <button 
                        className={`dropdown-item group-toggle ${expandedGroup === 'activity' ? 'expanded' : ''}`}
                        onClick={() => toggleGroup('activity')}
                      >
                        <Calendar size={18} />
                        <span>Activity</span>
                        <ChevronDown size={14} className={`group-chevron ${expandedGroup === 'activity' ? 'rotated' : ''}`} />
                      </button>
                      {expandedGroup === 'activity' && (
                        <div className="group-items">
                          {menuStructure.activity.map(renderMenuItem)}
                        </div>
                      )}
                      
                      {/* Money Group */}
                      <button 
                        className={`dropdown-item group-toggle ${expandedGroup === 'money' ? 'expanded' : ''}`}
                        onClick={() => toggleGroup('money')}
                      >
                        <Wallet size={18} />
                        <span>Money</span>
                        <ChevronDown size={14} className={`group-chevron ${expandedGroup === 'money' ? 'rotated' : ''}`} />
                      </button>
                      {expandedGroup === 'money' && (
                        <div className="group-items">
                          {menuStructure.money.map(renderMenuItem)}
                        </div>
                      )}
                      
                      {/* Account Group */}
                      <button 
                        className={`dropdown-item group-toggle ${expandedGroup === 'account' ? 'expanded' : ''}`}
                        onClick={() => toggleGroup('account')}
                      >
                        <User size={18} />
                        <span>Account</span>
                        <ChevronDown size={14} className={`group-chevron ${expandedGroup === 'account' ? 'rotated' : ''}`} />
                      </button>
                      {expandedGroup === 'account' && (
                        <div className="group-items">
                          {menuStructure.account.map(renderMenuItem)}
                        </div>
                      )}
                    </div>
                    
                    <div className="dropdown-divider" />
                    
                    <button className="dropdown-item logout" onClick={handleLogout}>
                      <LogOut size={18} />
                      <span>Log out</span>
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

    </>
  );
};

export default TopNav;
